import { deleteNotesForever } from "../db/actions";
import { db } from "../db/client";
import { useTabs } from "./Tabs";

/*
 * Empty notes go away by themselves (owner, 17 Oct: "when I create an empty note with zero content, automatically
 * delete it"). Empty = no title, no text, no tags, nothing in the body but empty paragraphs (so no task, picture,
 * file or other block), and never edited (version ≤ 1: a pushed title or body bumps it). Only notes made in Engram:
 * everything created before 2 Oct 2026 came from Evernote and is left alone, empty or not.
 *
 * - Leaving a note (another note, Back, another page) removes it 1.5 s later, after the editor's pending saves land,
 *   unless it's open again by then (or still in a tab). Only notes this device made, or over a day old: one made on
 *   the phone a minute ago may still be being typed into there.
 * - A sweep at start and every hour catches the rest (the app was closed on one, the widget's New note never typed
 *   into): this device's empty notes over 10 minutes old, anyone's over 24 hours, never the one on screen.
 *
 * Deleted outright, not moved to Trash: there was nothing to keep. Trashed empty notes go too. No toast.
 */

export const MADE_IN_ENGRAM_SINCE = Date.UTC(2026, 9, 2);
const MINE_AFTER = 10 * 60_000;
const ANYONES_AFTER = 24 * 3600_000;
const LEAVE_DELAY = 1500;
const KEY = "engram.newNotes";
const EMPTY_BODY = /^(?:\s|<p>(?:\s|<br\s*\/?>)*<\/p>)*$/i;

/** Notes this device made (id → created), kept two days. */
function mine(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

export function rememberNewNote(id: string, created: number) {
  try {
    const m = mine();
    for (const [k, t] of Object.entries(m)) if (created - t > 2 * ANYONES_AFTER) delete m[k];
    m[id] = created;
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* storage unavailable: such notes fall to the 24-hour rule */
  }
}

/** On screen: the open note, or a note kept in a tab (desktop, two or more tabs). */
function isOpen(id: string): boolean {
  if (new URLSearchParams(location.search).get("n") === id || location.pathname.includes(id)) return true;
  const { tabs } = useTabs.getState();
  return tabs.length > 1 && tabs.includes(id);
}

async function emptyNotes(ids?: string[]): Promise<{ id: string; created: number }[]> {
  const only = ids ? ` AND id IN (${ids.map(() => "?").join(",")})` : "";
  const rows = await db.query<{ id: string; created: number; body_html: string }>(
    `SELECT id, created, body_html FROM notes WHERE deleted = 0 AND version <= 1 AND title = '' AND body_text = '' AND tag_ids = '[]' AND created >= ?${only}`,
    [MADE_IN_ENGRAM_SINCE, ...(ids ?? [])],
  );
  return rows.filter((r) => EMPTY_BODY.test(r.body_html));
}

const due = (n: { id: string; created: number }, now: number, minAgeMine: number) => {
  const madeHere = mine()[n.id] !== undefined;
  return (madeHere && now - n.created >= minAgeMine) || now - n.created >= ANYONES_AFTER;
};

/** NotesPane: the note `id` was just left. */
export function leftNote(id: string) {
  setTimeout(() => {
    if (db.closed || isOpen(id)) return;
    void emptyNotes([id])
      .then((rows) => (rows.length && due(rows[0]!, Date.now(), 0) && !isOpen(id) ? deleteNotesForever([id]) : undefined))
      .catch((e) => db.closed || console.error("empty note", e));
  }, LEAVE_DELAY);
}

export async function sweepEmptyNotes(now = Date.now()) {
  if (db.closed) return;
  const gone = (await emptyNotes()).filter((n) => !isOpen(n.id) && due(n, now, MINE_AFTER)).map((n) => n.id);
  if (gone.length) await deleteNotesForever(gone.slice(0, 200));
}

let started = false;
/** Shell, once the database is open: a sweep shortly after start, then hourly. */
export function startEmptyNoteSweeps() {
  if (started) return;
  started = true;
  const run = () => void sweepEmptyNotes().catch((e) => db.closed || console.error("empty notes", e));
  setTimeout(run, 20_000);
  setInterval(run, 3600_000);
}
